import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Typography, Paper } from '@mui/material';
import { useAuth } from '@contexts/AuthContext';
import AdminLayout from '../layouts/AdminLayout';
import SettingsManagement from '../components/master-admin/SettingsManagement';
import NotificationsManagement from '../components/master-admin/NotificationsManagement';
import ReportsManagement from '../components/master-admin/ReportsManagement';
import ErrorsManagement from '../components/master-admin/ErrorsManagement';
import RequireMasterAdmin from '../auth/RequireMasterAdmin';
import { PageLoader } from '@shared/PageLoader';
import ErrorBoundary from '@shared/ErrorBoundary';
import RoleAwareHelp from '@shared/RoleAwareHelp';
import RoleAwareSettings from '@shared/RoleAwareSettings';
import Login from '@pages/common/Login';
import AdminLogin from '@pages/master-admin/components/AdminLogin';
import Register from '@pages/common/Register';
import RegisterClient from '@pages/common/RegisterClient';
import RegisterVeterinarian from '@pages/common/RegisterVeterinarian';
import RegisterClinic from '@pages/common/RegisterClinic';
import Activate from '@pages/common/Activate';
import ForgotPassword from '@pages/common/ForgotPassword';
import ResetPassword from '@pages/common/ResetPassword';
import SetPassword from '@pages/common/SetPassword';
import AccountType from '@pages/common/AccountType';
import RequireRole from '@shared/RequireRole';
import LandingOrRedirect from '@shared/LandingOrRedirect';
import ClientRouter from '@pages/client/ClientRouter';
import Forbidden from '@pages/common/Forbidden';
import ClinicsPartner from '@pages/client/components/ClinicsPartner';
import ClinicDetails from '@pages/client/components/ClinicDetails';
import AppointmentWizard from '../components/client/AppointmentWizard';
import PreCheckInWizard from '../components/client/PreCheckInWizard';
import SheltersPage from '@pages/client/components/SheltersPage';
import ShelterLayout from '@pages/client/components/ShelterLayout';
import ShelterPublicPage from '@pages/client/components/ShelterPublicPage';
import ShelterActivate from '@pages/common/ShelterActivate';
import Profile from '@pages/common/Profile';
import RecommendationsPage from '@pages/client/components/RecommendationsPage';
import TipsPage from '@pages/client/TipsPage';
import TipDetailPage from '@pages/client/TipDetailPage';
import VetProductsPage from '@pages/client/VetProductsPage';
import LabsPage from '@pages/client/components/LabsPage';
import ReviewsPage from '@pages/client/components/ReviewsPage';
import ClinicDashboard from '../components/clinic-admin/ClinicDashboard';
import AppointmentsManagementEnhanced from '../components/clinic-admin/AppointmentsManagementEnhanced';
import ClinicAppointmentsCalendar from '../components/clinic-admin/ClinicAppointmentsCalendar';
import PatientsManagement from '../components/clinic-admin/PatientsManagement';
import ClinicAnimalMedicalRecordPage from '../pages/clinic/ClinicAnimalMedicalRecordPage';
import VisitsManagement from '../components/clinic-admin/VisitsManagement';
import ClinicLabIntegrations from '@pages/clinic-admin/ClinicLabIntegrations';
import InternalSettings from '@pages/clinic-admin/components/InternalSettings';
import PaymentSettingsPage from '@pages/clinic-admin/components/PaymentSettingsPage';
import PrescriptionsManagementNew from '../components/clinic-admin/PrescriptionsManagementNew';
import ClientsManagement from '../components/clinic-admin/ClientsManagement';
import DoctorsManagement from '../components/clinic-admin/DoctorsManagement';
import ServicesManagement from '../components/clinic-admin/ServicesManagement';
import InventoryManagement from '../components/clinic-admin/InventoryManagement';
import PurchaseOrdersManagement from '../components/clinic-admin/PurchaseOrdersManagement';
import BillingManagement from '../components/clinic-admin/BillingManagement';
import ClinicSubscriptionsPage from '@pages/clinic-admin/components/Subscriptions';
import ClinicReportsManagement from '../components/clinic-admin/ReportsManagement';
import CommunicationManagement from '../components/clinic-admin/CommunicationManagement';
import AdministrationManagement from '../components/clinic-admin/AdministrationManagement';
import Animals from '@pages/client/components/Animals';
import MyPets from '@pages/client/components/MyPets';
import AnimalsListingPage from '@pages/client/components/AnimalsListingPage';
import VisitDetail from '@pages/client/components/VisitDetail';
import ClientDocuments from '@pages/client/components/ClientDocuments';
import ClientMessages from '@pages/client/components/ClientMessages';
import ClientInvoices from '@pages/client/components/ClientInvoices';
import ClientProfile from '@pages/client/components/ClientProfile';
import ClientNotifications from '@pages/client/components/ClientNotifications';
import MyAppointments from '@pages/client/components/MyAppointments';
import MyNotifications from '@pages/client/components/MyNotifications';
import PlanComparison from '@pages/client/PlanComparison';
import Notifications from '@pages/Notifications';
import ClinicNotificationsPage from '@pages/clinic-admin/components/Notifications';
import AdminErrors from '@pages/master-admin/components/AdminErrors';
import AdminImport from '@pages/master-admin/components/AdminImport';
import RomanianData from '@pages/master-admin/components/RomanianData';
import ONRCManagement from '@pages/master-admin/components/ONRCManagement';
import VeterinarianRouter from '@pages/veterinarian/VeterinarianRouter';
import ReceptionRouter from '@pages/receptionist/ReceptionRouter';
import VisitEncounter from '@pages/common/VisitEncounter';
import ShelterDashboard from '@pages/shelter-admin/ShelterDashboard';
import ShelterAnimalsPage from '@pages/shelter-admin/components/ShelterAnimalsPage';
import ShelterStaffPage from '@pages/shelter-admin/components/ShelterStaffPage';
import ShelterImportPage from '@pages/shelter-admin/components/ShelterImportPage';
import ShelterSettingsPage from '@pages/shelter-admin/components/ShelterSettingsPage';
import ShelterApplicationsPage from '@pages/shelter-admin/components/ShelterApplicationsPage';
import ShelterCalendarPage from '@pages/shelter-admin/components/ShelterCalendarPage';
import ShelterStaffActivate from '@pages/shelter-admin/components/ShelterStaffActivate';
import ShelterAnimalForm from '@components/shelter-admin/ShelterAnimalForm';
import ClinicProfile from '@pages/clinic-admin/components/ClinicProfile';
import DoctorAppointments from '@pages/veterinarian/components/DoctorAppointments';
import DoctorPatients from '@pages/veterinarian/components/DoctorPatients';
import DoctorTasks from '@pages/veterinarian/components/DoctorTasks';
import DoctorMessages from '@pages/veterinarian/components/DoctorMessages';
import DoctorPrescriptions from '@pages/veterinarian/components/DoctorPrescriptions';
import DoctorLab from '@pages/veterinarian/components/DoctorLab';
import DoctorTelemed from '@pages/veterinarian/components/DoctorTelemed';
import DoctorProcedures from '@pages/veterinarian/components/DoctorProcedures';
import DoctorReports from '@pages/veterinarian/components/DoctorReports';
import DoctorSettings from '@pages/veterinarian/components/DoctorSettings';

// Lazy load components
const MADashboard = lazy(() => import('../components/master-admin/DashboardNew'));
const MAUsers = lazy(() => import('../components/master-admin/UsersManagementNew'));
const MAClinics = lazy(() => import('../components/master-admin/ClinicsManagementNew'));
const MAVets = lazy(() => import('../components/master-admin/VeterinariansManagementNew'));
const MAAppointments = lazy(() => import('../components/master-admin/AppointmentsManagementNew'));
const MAPayments = lazy(() => import('../components/master-admin/PaymentsManagementNew'));
const MAPaymentSettingsPerClinic = lazy(() => import('../components/master-admin/PaymentSettingsPerClinic'));
const MASubscriptions = lazy(() => import('../components/master-admin/SubscriptionsManagement'));
const MASubscriptionPlans = lazy(() => import('../components/master-admin/SubscriptionPlansManagement'));
const MAHealthMonitoring = lazy(() => import('../components/master-admin/HealthMonitoring'));
const MAQueuesManagement = lazy(() => import('../components/master-admin/QueuesManagement'));
const MALogsManagement = lazy(() => import('../components/master-admin/LogsManagement'));
const MAApiKeysManagement = lazy(() => import('../components/master-admin/ApiKeysManagement'));
const MAWebhooksManagement = lazy(() => import('../components/master-admin/WebhooksManagement'));
const MAFeatureFlagsManagement = lazy(() => import('../components/master-admin/FeatureFlagsManagement'));
const MAMaintenanceMode = lazy(() => import('../components/master-admin/MaintenanceMode'));
const MAMenuItemsManagement = lazy(() => import('../components/master-admin/MenuItemsManagement'));
const MASheltersManagement = lazy(() => import('../pages/master-admin/components/SheltersManagement'));
const MATipsManagement = lazy(() => import('../components/master-admin/TipsManagement'));
const MAAutomation = lazy(() => import('../components/master-admin/AutomationManagement'));
const CampaignsPage = lazy(() => import('../pages/CampaignsPage'));

const NotificationsRoute = () => {
  const { user } = useAuth();

  if (user?.role === 'client') {
    return <Navigate to="/my-notifications" replace />;
  }

  return <Notifications />;
};

export const router = createBrowserRouter([
  {
    path: '/403',
    element: (
      <ErrorBoundary>
        <Forbidden />
      </ErrorBoundary>
    )
  },
  {
    path: '/login',
    element: (
      <ErrorBoundary>
        <Login />
      </ErrorBoundary>
    )
  },
  {
    path: '/login/admin',
    element: (
      <ErrorBoundary>
        <AdminLogin />
      </ErrorBoundary>
    )
  },
  {
    path: '/login/master-admin',
    element: (
      <ErrorBoundary>
        <Login />
      </ErrorBoundary>
    )
  },
  {
    path: '/register',
    element: (
      <ErrorBoundary>
        <Register />
      </ErrorBoundary>
    )
  },
  {
    path: '/account-type',
    element: (
      <ErrorBoundary>
        <AccountType />
      </ErrorBoundary>
    )
  },
  {
    path: '/register/client',
    element: (
      <ErrorBoundary>
        <RegisterClient />
      </ErrorBoundary>
    )
  },
  {
    path: '/register/veterinarian',
    element: (
      <ErrorBoundary>
        <RegisterVeterinarian />
      </ErrorBoundary>
    )
  },
  {
    path: '/register/clinic',
    element: (
      <ErrorBoundary>
        <RegisterClinic />
      </ErrorBoundary>
    )
  },
  {
    path: '/activate',
    element: (
      <ErrorBoundary>
        <Activate />
      </ErrorBoundary>
    )
  },
  {
    path: '/forgot-password',
    element: (
      <ErrorBoundary>
        <ForgotPassword />
      </ErrorBoundary>
    )
  },
  {
    path: '/reset-password',
    element: (
      <ErrorBoundary>
        <ResetPassword />
      </ErrorBoundary>
    )
  },
  {
    path: '/set-password',
    element: (
      <ErrorBoundary>
        <SetPassword />
      </ErrorBoundary>
    )
  },
  {
    path: '/shelter/activate',
    element: (
      <ErrorBoundary>
        <ShelterActivate />
      </ErrorBoundary>
    )
  },
  {
    path: '/shelter/staff/activate',
    element: (
      <ErrorBoundary>
        <ShelterStaffActivate />
      </ErrorBoundary>
    )
  },
  // Common routes for all authenticated users
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['client', 'veterinarian', 'clinic_admin', 'master_admin', 'shelter_admin', 'shelter_staff']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/profile',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <Profile />
          }
        ]
      },
      {
        path: '/help',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <RoleAwareHelp />
          }
        ]
      },
      {
        path: '/settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <RoleAwareSettings />
          }
        ]
      },
      {
        path: '/notifications',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <NotificationsRoute />
          }
        ]
      }
    ]
  },
  {
    path: '/master-admin',
    element: (
      <ErrorBoundary>
        <RequireMasterAdmin>
          <AdminLayout />
        </RequireMasterAdmin>
      </ErrorBoundary>
    ),
    children: [
      { 
        index: true, 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MADashboard />
          </Suspense>
        )
      },
      { 
        path: 'users', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAUsers />
          </Suspense>
        )
      },
      { 
        path: 'users/medical', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAUsers />
          </Suspense>
        )
      },
      { 
        path: 'users/clients', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAUsers />
          </Suspense>
        )
      },
      { 
        path: 'clinics', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAClinics />
          </Suspense>
        )
      },
      { 
        path: 'shelters', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MASheltersManagement />
          </Suspense>
        )
      },
      { 
        path: 'vets', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAVets />
          </Suspense>
        )
      },
      { 
        path: 'appointments', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAAppointments />
          </Suspense>
        )
      },
      { 
        path: 'payments', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAPayments />
          </Suspense>
        )
      },
      {
        path: 'payment-settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAPaymentSettingsPerClinic />
          </Suspense>
        )
      },
      { 
        path: 'subscriptions', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MASubscriptions />
          </Suspense>
        )
      },
      { 
        path: 'plans/clinics', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MASubscriptionPlans />
          </Suspense>
        )
      },
      { 
        path: 'plans/users', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MASubscriptionPlans />
          </Suspense>
        )
      },
      {
        path: 'notifications',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotificationsManagement />
          </Suspense>
        )
      },
      {
        path: 'errors',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ErrorsManagement />
          </Suspense>
        )
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SettingsManagement />
          </Suspense>
        )
      },
      {
        path: 'reports',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReportsManagement />
          </Suspense>
        )
      },
      { 
        path: 'automation', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAAutomation />
          </Suspense>
        )
      },
      // Operatiuni & Observabilitate
      { 
        path: 'ops/health', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAHealthMonitoring />
          </Suspense>
        )
      },
      { 
        path: 'ops/queues', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAQueuesManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/logs', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MALogsManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/api-keys', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAApiKeysManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/webhooks', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAWebhooksManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/feature-flags', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAFeatureFlagsManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/menu-items', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAMenuItemsManagement />
          </Suspense>
        )
      },
      { 
        path: 'ops/maintenance', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MAMaintenanceMode />
          </Suspense>
        )
      },
      { 
        path: 'content/tips', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <MATipsManagement />
          </Suspense>
        )
      },
    ],
  },
  // Client routes
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['client']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/dashboard',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientRouter />
          }
        ]
      },
      {
        path: '/animals',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <Animals />
          },
          {
            path: 'new',
            element: <Animals />
          },
          {
            path: ':id',
            element: <Animals />
          }
        ]
      },
      {
        path: '/my-pets',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { index: true, element: <MyPets /> },
          { path: 'new', element: <MyPets /> },
          { path: ':id', element: <MyPets /> }
        ]
      },
      {
        path: '/adoptie',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [{ index: true, element: <AnimalsListingPage /> }]
      },
      {
        path: '/client/clinics',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClinicsPartner />
          },
          {
            path: ':clinicId',
            element: <ClinicDetails />
          },
          {
            path: ':clinicId/appointments/new',
            element: <AppointmentWizard />
          },
          {
            path: 'precheck/:token',
            element: <PreCheckInWizard />
          }
        ]
      },
      {
        path: '/client/shelters',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <SheltersPage />
          }
        ]
      },
      {
        path: '/shelters/:shelterId',
        element: <ShelterPublicPage />
      },
      {
        path: '/client/shelters/:shelterId',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ShelterLayout />
          },
          {
            path: 'animals',
            element: <ShelterLayout />
          },
          {
            path: 'contact',
            element: <ShelterLayout />
          },
          {
            path: 'donations',
            element: <ShelterLayout />
          }
        ]
      },
      {
        path: '/recomandari',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <RecommendationsPage />
          }
        ]
      },
            {
              path: '/tips',
              element: (
                <ErrorBoundary>
                  <AdminLayout />
                </ErrorBoundary>
              ),
              children: [
                {
                  index: true,
                  element: <TipsPage />
                },
                {
                  path: ':slug',
                  element: <TipDetailPage />
                }
              ]
            },
            {
              path: '/produse-medicinale',
              element: (
                <ErrorBoundary>
                  <AdminLayout />
                </ErrorBoundary>
              ),
              children: [
                {
                  index: true,
                  element: <VetProductsPage />
                }
              ]
            },
            {
              path: '/analize',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <LabsPage />
          }
        ]
      },
      {
        path: '/campanii',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: (
              <Suspense fallback={<PageLoader />}>
                <CampaignsPage />
              </Suspense>
            )
          }
        ]
      },
      {
        path: '/reviews',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ReviewsPage />
          }
        ]
      },
      {
        path: '/plans',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <PlanComparison />
          }
        ]
      },
      {
        path: '/subscriptions',
        element: <Navigate to="/plans" replace />
      },
      {
        path: '/subscription',
        element: <Navigate to="/plans" replace />
      },
      {
        path: '/appointments',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <MyAppointments />
          },
          {
            path: ':visitId',
            element: <VisitDetail />
          }
        ]
      },
      {
        path: '/visits',
        element: <Navigate to="/appointments" replace />
      },
      {
        path: '/medical-records',
        element: <Navigate to="/appointments" replace />
      },
      {
        path: '/documents',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientDocuments />
          }
        ]
      },
      {
        path: '/messages',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientMessages />
          }
        ]
      },
      {
        path: '/invoices',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientInvoices />
          }
        ]
      },
      {
        path: '/my-notifications',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <MyNotifications />
          }
        ]
      },
      {
        path: '/profile-settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientProfile />
          }
        ]
      },
      {
        path: '/notifications-settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientNotifications />
          }
        ]
      },
      {
        path: '/client/animals',
        element: <Navigate to="/animals" replace />
      },
      {
        path: '/client/pets',
        element: <Navigate to="/animals" replace />
      }
    ]
  },

  // Shelter routes
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['shelter_admin', 'shelter_staff']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/shelter',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ShelterDashboard />
          }
        ]
      },
      {
        path: '/shelter/animals',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ShelterAnimalsPage />
          },
          {
            path: 'new',
            element: (
              <ErrorBoundary>
                <RequireRole allowed={['shelter_admin']} />
              </ErrorBoundary>
            ),
            children: [
              {
                element: (
                  <ErrorBoundary>
                    <AdminLayout />
                  </ErrorBoundary>
                ),
                children: [
                  {
                    index: true,
                    element: <ShelterAnimalForm />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: '/shelter/staff',
        element: (
          <ErrorBoundary>
            <RequireRole allowed={['shelter_admin']} />
          </ErrorBoundary>
        ),
        children: [
          {
            element: (
              <ErrorBoundary>
                <AdminLayout />
              </ErrorBoundary>
            ),
            children: [
              {
                index: true,
                element: <ShelterStaffPage />
              }
            ]
          }
        ]
      },
      {
        path: '/shelter/import',
        element: (
          <ErrorBoundary>
            <RequireRole allowed={['shelter_admin']} />
          </ErrorBoundary>
        ),
        children: [
          {
            element: (
              <ErrorBoundary>
                <AdminLayout />
              </ErrorBoundary>
            ),
            children: [
              {
                index: true,
                element: <ShelterImportPage />
              }
            ]
          }
        ]
      },
      {
        path: '/shelter/settings',
        element: (
          <ErrorBoundary>
            <RequireRole allowed={['shelter_admin']} />
          </ErrorBoundary>
        ),
        children: [
          {
            element: (
              <ErrorBoundary>
                <AdminLayout />
              </ErrorBoundary>
            ),
            children: [
              {
                index: true,
                element: <ShelterSettingsPage />
              }
            ]
          }
        ]
      },
      {
        path: '/shelter/applications',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ShelterApplicationsPage />
          }
        ]
      },
      {
        path: '/shelter/calendar',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ShelterCalendarPage />
          }
        ]
      }
    ]
  },

  // Doctor routes
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['doctor', 'veterinarian']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/visits/:id/encounter',
        element: (
          <ErrorBoundary>
            <VisitEncounter />
          </ErrorBoundary>
        )
      },
      {
        path: '/doctor/*',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <VeterinarianRouter />
          }
        ]
      },
      {
        path: '/doctor/appointments',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorAppointments />
          }
        ]
      },
      {
        path: '/doctor/patients',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorPatients />
          }
        ]
      },
      {
        path: '/doctor/patients/:id',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorPatients />
          }
        ]
      },
      {
        path: '/doctor/patients/:animalId/medical',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [{ index: true, element: <ClinicAnimalMedicalRecordPage /> }],
      },
      {
        path: '/doctor/tasks',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorTasks />
          }
        ]
      },
      {
        path: '/doctor/messages',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorMessages />
          }
        ]
      },
      {
        path: '/doctor/prescriptions',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorPrescriptions />
          }
        ]
      },
      {
        path: '/doctor/lab',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorLab />
          }
        ]
      },
      {
        path: '/doctor/telemed',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorTelemed />
          }
        ]
      },
      {
        path: '/doctor/procedures',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorProcedures />
          }
        ]
      },
      {
        path: '/doctor/reports',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorReports />
          }
        ]
      },
      {
        path: '/doctor/settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorSettings />
          }
        ]
      }
    ]
  },

  // Reception routes (pentru personal de receptie)
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['reception', 'clinic_admin']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/receptie/*',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            path: '*',
            element: <ReceptionRouter />
          }
        ]
      },
      {
        path: '/reception',
        element: <Navigate to="/receptie" replace />
      },
      {
        path: '/reception/*',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            path: '*',
            element: <ReceptionRouter />
          }
        ]
      }
    ]
  },

  // Clinic Admin routes
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['clinic_admin']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/clinic',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClinicDashboard />
          }
        ]
      },
      {
        path: '/clinic/appointments',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AppointmentsManagementEnhanced />
          }
        ]
      },
      {
        path: '/clinic/appointments/calendar',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClinicAppointmentsCalendar />
          }
        ]
      },
      {
        path: '/clinic/patients',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <PatientsManagement />,
          },
          {
            path: ':animalId/medical',
            element: <ClinicAnimalMedicalRecordPage />,
          },
        ],
      },
      {
        path: '/clinic/inventory',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <InventoryManagement />
          }
        ]
      },
      {
        path: '/clinic/profile',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClinicProfile />
          }
        ]
      },
      {
        path: '/clinic/settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <InternalSettings />
          }
        ]
      },
      {
        path: '/clinic-settings/lab-integrations',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { index: true, element: <ClinicLabIntegrations /> }
        ]
      },
      {
        path: '/clinic/payment-settings',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { index: true, element: <PaymentSettingsPage /> }
        ]
      },
      {
        path: '/clinic/doctors',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorsManagement />
          }
        ]
      },
      {
        path: '/clinic/reports',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <ClinicReportsManagement />
          }
        ]
      },
      {
        path: '/clinic/visits',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <VisitsManagement />
          }
        ]
      },
      {
        path: '/clinic/visits/:id',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <VisitsManagement />
          }
        ]
      },
      {
        path: '/clinic/prescriptions',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <PrescriptionsManagementNew />
          },
          { 
            path: 'new', 
            element: <PrescriptionsManagementNew />
          }
        ]
      },
      {
        path: '/clinic/medications',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <InventoryManagement />
          }
        ]
      },
      {
        path: '/clinic/clients',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClientsManagement />
          }
        ]
      },
      {
        path: '/clinic/doctors/:id/schedule',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <DoctorsManagement />
          }
        ]
      },
      {
        path: '/clinic/services',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ServicesManagement />
          }
        ]
      },
      {
        path: '/clinic/service-packages',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ServicesManagement />
          }
        ]
      },
      {
        path: '/clinic/purchase-orders',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <PurchaseOrdersManagement />
          }
        ]
      },
      {
        path: '/clinic/billing',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <BillingManagement />
          }
        ]
      },
      {
        path: '/clinic/subscriptions',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [{ index: true, element: <ClinicSubscriptionsPage /> }]
      },
      {
        path: '/clinic/notifications',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ClinicNotificationsPage />
          }
        ]
      },
      {
        path: '/clinic/approvals',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdministrationManagement />
          }
        ]
      },
      {
        path: '/clinic/nps',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <CommunicationManagement />
          }
        ]
      },
      {
        path: '/clinic/audit',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdministrationManagement />
          }
        ]
      },
      {
        path: '/clinic/users',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdministrationManagement />
          }
        ]
      },
      {
        path: '/clinic/integrations',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdministrationManagement />
          }
        ]
      },
      {
        path: '/clinic/gdpr',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdministrationManagement />
          }
        ]
      }
    ]
  },

  // Master Admin routes
  {
    element: (
      <ErrorBoundary>
        <RequireRole allowed={['master_admin']} />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/admin/errors',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdminErrors />
          }
        ]
      },
      {
        path: '/admin/import',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <AdminImport />
          }
        ]
      },
      {
        path: '/admin/romanian-data',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <RomanianData />
          }
        ]
      },
      {
        path: '/admin/onrc',
        element: (
          <ErrorBoundary>
            <AdminLayout />
          </ErrorBoundary>
        ),
        children: [
          { 
            index: true, 
            element: <ONRCManagement />
          }
        ]
      }
    ]
  },
  // Redirect old ?module= routes to new structure
  {
    path: '/master-admin-old',
    element: <Navigate to="/master-admin" replace />
  },
  // Default / fallback
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <LandingOrRedirect />
      </ErrorBoundary>
    )
  },
  // Catch-all route for 404
  {
    path: '*',
    element: (
      <ErrorBoundary>
        <div>404 - Pagina nu a fost gasita</div>
      </ErrorBoundary>
    )
  }
]);
